import { useCallback } from 'react';

  /**
   * 字段编辑激活选项
   */
export interface ActivateFieldEditOptions {
  /**
   * 字段元素（DOM 元素）
   */
  element: HTMLElement;
  
  /**
   * 原始值（保留接口兼容性，但不再使用）
   */
  originalValue?: any;
  
  /**
   * 激活前回调（在聚焦之前执行）
   * 用于处理特定逻辑，如打开 InputRecommend、设置组件状态等
   */
  onBeforeActivate?: () => void;
  
  /**
   * 激活后回调（在聚焦之后执行）
   * 用于处理特定逻辑，如计算位置、全选文本等
   */
  onAfterActivate?: () => void;
  
  /**
   * 是否自动聚焦元素
   * 默认为 true
   */
  autoFocus?: boolean;
  
  /**
   * 是否全选文本（仅对输入框有效）
   * 默认为 false
   */
  selectText?: boolean;
  
  /**
   * 双击时的鼠标X坐标（相对于字段元素）
   * 如果提供，将根据此坐标定位光标位置，而不是全选文本
   */
  clickX?: number;
}

/**
 * 字段编辑完成选项
 */
export interface FinishFieldEditOptions {
  /**
   * 字段元素（DOM 元素）
   */
  element?: HTMLElement;
  
  /**
   * 完成时回调
   * 用于处理特定逻辑，如提交更新、清除组件状态等
   */
  onFinish?: () => void;
  
  /**
   * 是否移除焦点
   * 默认为 false
   */
  shouldBlur?: boolean;
  
  /**
   * 完成后的状态（保留接口兼容性，但不再使用）
   */
  finalState?: 'selected' | null;
}

/**
 * 统一字段编辑状态管理 Hook
 * 
 * 功能：
 * 1. 统一管理所有输入框的编辑状态激活和完成
 * 2. 整合通用逻辑（聚焦、状态设置、原始值保存）
 * 3. 通过回调支持特定逻辑（InputRecommend、位置计算等）
 * 4. 解决焦点与状态不同步的问题
 * 
 * @returns 激活和完成字段编辑的方法
 * 
 * @example
 * ```tsx
 * const { activateFieldEdit, finishFieldEdit } = useUnifiedFieldEdit();
 * 
 * // 激活编辑
 * activateFieldEdit({
 *   element: inputElement,
 *   originalValue: '原始值',
 *   onBeforeActivate: () => {
 *     // 打开 InputRecommend 等特定逻辑
 *   },
 *   onAfterActivate: () => {
 *     // 计算位置等特定逻辑
 *   }
 * });
 * 
 * // 完成编辑
 * finishFieldEdit({
 *   element: inputElement,
 *   onFinish: () => {
 *     // 提交更新等特定逻辑
 *   }
 * });
 * ```
 */
export const useUnifiedFieldEdit = () => {
  // 根据点击位置设置光标位置（类似 TableCell 的实现）
  const setCursorPositionFromClick = useCallback((input: HTMLInputElement | HTMLTextAreaElement, clickX: number) => {
    try {
      const text = input.value || '';
      
      // 先清除可能存在的文本选择
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
      
      // 获取输入框的样式
      const styles = window.getComputedStyle(input);
      
      // 计算相对于输入框的点击位置
      // clickX 已经是相对于输入框的坐标（从 getBoundingClientRect().left 计算得出）
      // 需要减去左边距（padding）和边框（border）
      const inputPaddingLeft = parseFloat(styles.paddingLeft || '0');
      const inputBorderLeft = parseFloat(styles.borderLeftWidth || '0');
      const relativeX = clickX - inputPaddingLeft - inputBorderLeft;
      
      // 创建一个临时元素来测量文本宽度
      const measureElement = document.createElement('span');
      measureElement.style.position = 'absolute';
      measureElement.style.visibility = 'hidden';
      measureElement.style.whiteSpace = 'pre';
      measureElement.style.font = styles.font;
      measureElement.style.fontSize = styles.fontSize;
      measureElement.style.fontFamily = styles.fontFamily;
      measureElement.style.fontWeight = styles.fontWeight;
      measureElement.style.fontStyle = styles.fontStyle;
      measureElement.style.letterSpacing = styles.letterSpacing;
      measureElement.style.fontVariantNumeric = styles.fontVariantNumeric;
      document.body.appendChild(measureElement);
      
      // 计算光标位置：二分查找最接近的位置
      let cursorPosition = 0;
      
      if (text.length === 0) {
        cursorPosition = 0;
      } else {
        // 使用二分查找找到最接近的位置
        let left = 0;
        let right = text.length;
        
        while (left < right) {
          const mid = Math.floor((left + right) / 2);
          measureElement.textContent = text.substring(0, mid);
          const width = measureElement.offsetWidth;
          
          if (width < relativeX) {
            left = mid + 1;
          } else {
            right = mid;
          }
        }
        
        // 检查左右两个位置，选择更接近的
        const pos1 = Math.max(0, left - 1);
        const pos2 = Math.min(text.length, left);
        
        measureElement.textContent = text.substring(0, pos1);
        const width1 = measureElement.offsetWidth;
        measureElement.textContent = text.substring(0, pos2);
        const width2 = measureElement.offsetWidth;
        
        const dist1 = Math.abs(width1 - relativeX);
        const dist2 = Math.abs(width2 - relativeX);
        
        cursorPosition = dist1 < dist2 ? pos1 : pos2;
      }
      
      // 设置光标位置（这会清除文本选择）
      if (input.setSelectionRange) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
      
      // 清理临时元素
      if (measureElement.parentNode) {
        document.body.removeChild(measureElement);
      }
    } catch (error) {
      // 如果出现错误，静默处理（避免影响正常编辑功能）
      console.warn('Failed to set cursor position:', error);
    }
  }, []);

  /**
   * 激活字段编辑状态
   * 
   * 执行顺序：
   * 1. 检查当前状态，如果是 selected，清除 readOnly
   * 2. 调用 onBeforeActivate（如果有）
   * 3. 设置状态为 editing
   * 4. 保存原始值
   * 5. 聚焦元素（如果 autoFocus 为 true）
   * 6. 全选文本（如果 selectText 为 true）或定位光标（如果 clickX 提供）
   * 7. 调用 onAfterActivate（如果有）
   */
  const activateFieldEdit = useCallback((options: ActivateFieldEditOptions) => {
    const {
      element,
      onBeforeActivate,
      onAfterActivate,
      autoFocus = true,
      selectText = false,
      clickX,
    } = options;

    if (!element) {
      console.warn('[useUnifiedFieldEdit] element is required');
      return;
    }

    // 清除 readOnly 属性（如果有）
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const inputElement = element as HTMLInputElement | HTMLTextAreaElement;
      inputElement.readOnly = false;
    }

    // 执行激活前回调（用于处理特定逻辑，如打开 InputRecommend）
    if (onBeforeActivate) {
      onBeforeActivate();
    }

    // 聚焦元素（如果需要）
    if (autoFocus) {
      // 使用 requestAnimationFrame 确保 DOM 更新完成
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.focus();

          // 如果是输入框，处理文本选择或光标定位
          if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
            const inputElement = element as HTMLInputElement | HTMLTextAreaElement;
            
            // 如果提供了 clickX，根据坐标定位光标（双击场景）
            if (clickX !== undefined && inputElement.type !== 'number') {
              // 立即设置光标位置（同步执行，覆盖浏览器默认行为）
              setCursorPositionFromClick(inputElement, clickX);
              
              // 如果同步设置没有生效，使用 setTimeout 再次设置
              setTimeout(() => {
                if (inputElement === document.activeElement) {
                  setCursorPositionFromClick(inputElement, clickX);
                }
              }, 0);
              
              // 再次使用 requestAnimationFrame 确保光标位置设置成功
              requestAnimationFrame(() => {
                if (inputElement === document.activeElement) {
                  setCursorPositionFromClick(inputElement, clickX);
                }
              });
            }
            // 否则，如果需要全选文本（Enter 键场景）
            else if (selectText && inputElement.type !== 'number') {
              setTimeout(() => {
                inputElement.select();
              }, 0);
            }
          }

          // 执行激活后回调（用于处理特定逻辑，如计算位置）
          if (onAfterActivate) {
            onAfterActivate();
          }
        });
      });
    } else {
      // 如果不自动聚焦，直接处理文本选择或光标定位（元素已经聚焦）
      if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        const inputElement = element as HTMLInputElement | HTMLTextAreaElement;
        
        // 如果提供了 clickX，根据坐标定位光标（双击场景）
        if (clickX !== undefined && inputElement.type !== 'number') {
          // 立即设置光标位置（同步执行，覆盖浏览器默认行为）
          setCursorPositionFromClick(inputElement, clickX);
          
          // 如果同步设置没有生效，使用 requestAnimationFrame 再次设置
          requestAnimationFrame(() => {
            if (inputElement === document.activeElement) {
              setCursorPositionFromClick(inputElement, clickX);
            }
          });
        }
        // 否则，如果需要全选文本（Enter 键场景）
        else if (selectText && inputElement.type !== 'number') {
          setTimeout(() => {
            inputElement.select();
          }, 0);
        }
      }
      
      // 执行激活后回调（用于处理特定逻辑，如计算位置）
      if (onAfterActivate) {
        setTimeout(() => {
          onAfterActivate();
        }, 0);
      }
    }
  }, [setCursorPositionFromClick]);

  /**
   * 完成字段编辑
   * 
   * 执行顺序：
   * 1. 如果需要移除焦点，先移除焦点
   * 2. 调用 onFinish（如果有）
   * 3. 设置最终状态（selected 或 null）
   * 4. 清除原始值
   */
  const finishFieldEdit = useCallback((options: FinishFieldEditOptions) => {
    const {
      element,
      onFinish,
      shouldBlur = false,
    } = options;

    // 如果没有提供元素，尝试从 document.activeElement 获取
    let targetElement = element;
    if (!targetElement) {
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && activeElement.hasAttribute('data-field')) {
        targetElement = activeElement;
      }
    }

    if (!targetElement) {
      console.warn('[useUnifiedFieldEdit] No element found to finish editing');
      return;
    }

    // 如果需要移除焦点，先同步移除焦点
    if (shouldBlur) {
      const activeElement = document.activeElement;
      if (activeElement) {
        // 检查是否在表格容器中
        const isTableInput = activeElement.closest('.breakdown-table-container') ||
                            activeElement.closest('.scene-container') ||
                            activeElement.closest('.element-table');
        
        if (isTableInput) {
          // 处理 INPUT 和 SELECT 元素
          if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'SELECT') {
            (activeElement as HTMLElement).blur();
          }
          // 处理下拉框按钮
          else if (activeElement.classList.contains('excel-cell-button') ||
                   activeElement.classList.contains('scene-detail-select-button')) {
            (activeElement as HTMLElement).blur();
          }
        }
      }
    }

    // 执行完成回调（用于处理特定逻辑，如提交更新）
    if (onFinish) {
      onFinish();
    }

    // 取消文本选择（如果有）
    if (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA') {
      const inputElement = targetElement as HTMLInputElement | HTMLTextAreaElement;
      // 只在非 number 类型的输入框上调用 setSelectionRange
      if (inputElement.type !== 'number') {
        // 将光标移动到文本末尾，取消选择
        const length = inputElement.value.length;
        inputElement.setSelectionRange(length, length);
      }
    }
  }, []);

  return {
    activateFieldEdit,
    finishFieldEdit,
    setCursorPositionFromClick, // 导出光标定位函数，供外部使用
  };
};

